import React from 'react';
import { Info } from 'lucide-react';

/**
 * MOSAIC-NY1 · Pillar 4 · Multiple-Measures Disclaimer
 *
 * Canonical, locked wording (byte-identical across Digital, Print, USB).
 *
 * Owner-authorized revision 2026-10-13 (Incident 10 of parity history):
 * previous wording referenced "official WIDA ACCESS scores"; replaced per
 * explicit owner directive with independent MOSAIC wording below.
 *
 * The compact variant presents a short summary with an accessible disclosure
 * (native <details>/<summary>) that reveals the complete locked wording
 * inline — keyboard-, touch-, and screen-reader-accessible. The disclosure
 * MUST NEVER shorten or reinterpret the locked wording.
 */
export const MULTIPLE_MEASURES_DISCLAIMER_TEXT =
  "MOSAIC results provide practice and instructional indicators, not official state language proficiency scores or eligibility determinations. Review alongside classroom performance and other measures.";

const MultipleMeasuresDisclaimer = ({ variant = 'full', className = '' }) => {
  // Compact summary + inline accessible disclosure (click/tap/keyboard/SR).
  // Uses native <details>/<summary> so it works with mouse, keyboard
  // (Tab + Enter/Space), touch, and is announced by assistive tech.
  if (variant === 'compact') {
    return (
      <div
        className={`flex items-start gap-2 bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 ${className}`}
        data-testid="multiple-measures-disclaimer-compact"
      >
        <Info size={14} className="flex-shrink-0 mt-0.5 text-blue-600" />
        <div className="flex-1">
          <p>
            <span className="font-semibold">Interpreting MOSAIC Results:</span>{' '}
            Practice and instructional indicators, not official state language proficiency scores. Review alongside classroom performance and other measures.
          </p>
          <details className="mt-1.5 group">
            <summary
              className="inline-flex items-center gap-1 cursor-pointer underline text-blue-800 hover:text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
              data-testid="multiple-measures-learn-more"
              aria-label="Show full Multiple-Measures guidance"
            >
              <span className="group-open:hidden">Learn more</span>
              <span className="hidden group-open:inline">Hide details</span>
            </summary>
            <p
              className="mt-2 leading-relaxed text-blue-900 bg-white/60 border border-blue-200 p-2"
              data-testid="multiple-measures-full-text"
            >
              {MULTIPLE_MEASURES_DISCLAIMER_TEXT}
            </p>
          </details>
        </div>
      </div>
    );
  }

  // Full locked wording block (detail / report / parent surfaces).
  return (
    <div
      className={`flex items-start gap-3 bg-blue-50 border-l-4 border-blue-500 p-4 ${className}`}
      data-testid="multiple-measures-disclaimer"
    >
      <Info size={18} className="flex-shrink-0 mt-0.5 text-blue-600" />
      <div className="text-sm text-blue-900">
        <div className="font-semibold mb-1">Interpreting MOSAIC Results</div>
        <p className="leading-relaxed">{MULTIPLE_MEASURES_DISCLAIMER_TEXT}</p>
      </div>
    </div>
  );
};

export default MultipleMeasuresDisclaimer;
